import React from 'react'
import { Box, Card, Typography } from '@mui/material'
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import GenericButton from '../../../../components/GenericButton'
import { ButtonType } from '../../../../components/GenericButton/styles'
import { grey, indigo } from '../../../../colors'
import { RESULTS_ACCESS_REASONS, type ResultsAccessReason } from './reasons'

export interface ResultsAccessCardSecondaryAction {
  label: string
  onClick?: () => void
  href?: string
}

export interface ResultsAccessCardProps {
  reason: ResultsAccessReason
  align?: 'center' | 'left'
  /** `permission` reason's meta row — the caller's active role name. */
  roleName?: string | null
  /** `insufficientResponses` reason's progress row. */
  minResponses?: { current: number, min: number } | null
  backLabel?: string
  onBack?: () => void
  secondaryAction?: ResultsAccessCardSecondaryAction
}

/** Progress track tokens from the standalone Results Access Card design. */
const RESP_TRACK_BG = '#E7E9EE'
const RESP_TRACK_WIDTH = 188
const RESP_TRACK_HEIGHT = 7

const ResultsAccessCard: React.FC<ResultsAccessCardProps> = ({
  reason,
  align = 'center',
  roleName,
  minResponses,
  backLabel = 'Go back',
  onBack,
  secondaryAction
}) => {
  const preset = RESULTS_ACCESS_REASONS[reason]
  const { Icon } = preset
  const isLeftAligned = align === 'left'
  const showActions = Boolean(onBack) || Boolean(secondaryAction)
  const progressPct = minResponses != null && minResponses.min > 0
    ? Math.min(100, (minResponses.current / minResponses.min) * 100)
    : 0

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isLeftAligned ? 'flex-start' : 'center',
          textAlign: isLeftAligned ? 'left' : 'center',
          // Standalone design: 64px vertical / 32px horizontal (left-align uses 40).
          padding: isLeftAligned ? '64px 40px' : '64px 32px'
        }}
      >
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '17px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: preset.badgeBackground,
            color: preset.badgeForeground,
            marginBottom: '22px'
          }}
        >
          <Icon sx={{ fontSize: 30 }} />
        </Box>

        <Typography
          sx={{
            fontSize: '21px',
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: grey[900],
            margin: '0 0 10px'
          }}
        >
          {preset.title}
        </Typography>

        <Typography
          sx={{
            maxWidth: isLeftAligned ? 560 : 460,
            fontSize: '15.5px',
            lineHeight: 1.6,
            color: grey[500],
            margin: 0
          }}
        >
          {preset.body}
        </Typography>

        {reason === 'permission' && roleName && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              marginTop: '18px',
              padding: '9px 15px',
              backgroundColor: grey[100],
              border: `1px solid ${grey[350]}`,
              borderRadius: '10px',
              whiteSpace: 'nowrap'
            }}
          >
            <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: grey[400] }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: grey[600] }}>Your role:</Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: grey[900] }}>{roleName}</Typography>
          </Box>
        )}

        {reason === 'insufficientResponses' && minResponses && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '9px',
              marginTop: '18px',
              padding: '12px 15px',
              backgroundColor: grey[100],
              border: `1px solid ${grey[350]}`,
              borderRadius: '10px',
              whiteSpace: 'nowrap',
              '& > *': { flexShrink: 0 }
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 16, color: grey[400] }} />
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: grey[900] }}>
              {minResponses.current} of {minResponses.min}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: grey[600] }}>responses</Typography>
            <Box
              sx={{
                width: RESP_TRACK_WIDTH,
                height: RESP_TRACK_HEIGHT,
                borderRadius: '5px',
                backgroundColor: RESP_TRACK_BG,
                overflow: 'hidden',
                marginLeft: '4px'
              }}
              role="progressbar"
              aria-valuenow={Math.round(progressPct)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${minResponses.current} of ${minResponses.min} responses`}
            >
              <Box
                sx={{
                  width: `${progressPct}%`,
                  height: '100%',
                  borderRadius: '5px',
                  backgroundColor: indigo[500]
                }}
              />
            </Box>
          </Box>
        )}

        {showActions && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '30px' }}>
            {onBack && (
              <GenericButton
                label={backLabel}
                variant="outlined"
                buttonType={ButtonType.Secondary}
                onClick={onBack}
              />
            )}
            {secondaryAction && (
              secondaryAction.href
                ? (
                  <GenericButton
                    label={secondaryAction.label}
                    variant="text"
                    buttonType={ButtonType.Ghost}
                    onClick={() => { window.location.href = secondaryAction.href ?? '' }}
                  />
                  )
                : (
                  <GenericButton
                    label={secondaryAction.label}
                    variant="contained"
                    buttonType={ButtonType.Primary}
                    onClick={secondaryAction.onClick}
                  />
                  )
            )}
          </Box>
        )}
      </Box>
    </Card>
  )
}

export default ResultsAccessCard
